// ============================================================
// EventPlatform.jsx  –  All 14 screens
// Form-filler role: schools/universities
// Event-creator role: organizers
// Isolation enforced via userCredentials (user_id + selectedentity + tenants_id)
// ============================================================
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Fetchexecutequery, Fetchheaderdata, Fetchupdatequery } from '../../../services/process/BRS1/brsService7';
import { CreateData } from '../../../services/process/process_data/process_data';
import {
  LayoutDashboard, Calendar, FileText, Bell, CheckCircle, Clock,
  TrendingUp, Users, MapPin, Search, Filter, ChevronDown, ChevronRight,
  ChevronLeft, Star, Share2, Eye, Edit3, Trash2, Copy, BarChart2,
  PieChart, Globe, Mail, Phone, Link, Upload, Plus, Minus, X,
  AlertCircle, Check, Download, Send, RefreshCw, ArrowRight,
  Building, BookOpen, Award, Info, HelpCircle, Layers, Target,
  Activity, Flag, Archive, Zap, List, Grid, Settings, LogOut,
  ChevronUp, MoreVertical, ExternalLink, Save, AlertTriangle,
  ThumbsUp, ThumbsDown, MessageSquare, Paperclip, RotateCcw,
  UserPlus, Shield, Bookmark, Hash
} from "lucide-react";

// ── Shared helpers ─────────────────────────────────────────────────────────
const getUserCredentials = () =>
  JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

const getFormattedDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

// ── User credentials from sessionStorage (Production Mode) ──────────────────
/* MOCK CREDENTIALS - COMMENTED OUT FOR PRODUCTION
const MOCK_CREDENTIALS = {
  user_id: 101,
  user_name: "admin_user",
  tenants_id: 1,
  groups_id: 2,
  selectedentity: 10,
  role: "form_filler",
  display_name: "St. Xavier's School",
};
*/

// ── Colour palette & design tokens ────────────────────────────────────────
const C = {
  primary:   "#14253d",
  accent:    "#2563eb",
  accent2:   "#0f766e",
  warn:      "#d97706",
  danger:    "#dc2626",
  success:   "#16a34a",
  purple:    "#7c3aed",
  bg:        "#eef3f8",
  card:      "#ffffff",
  border:    "#d7e0ea",
  text:      "#0f172a",
  muted:     "#64748b",
  light:     "#e8eef5",
};

const AppShell = ({ children, style = {} }) => (
  <div
    style={{
      minHeight: "100vh",
      width: "100%",
      background:
        "radial-gradient(circle at top left, rgba(37,99,235,.08), transparent 30%), radial-gradient(circle at top right, rgba(15,118,110,.08), transparent 26%), linear-gradient(180deg, #f7fafc 0%, #eef4fa 100%)",
      color: C.text,
      fontFamily: "'Segoe UI', sans-serif",
      ...style,
    }}
  >
    {children}
  </div>
);

const PageWrap = ({ children, wide = false, style = {} }) => (
  <div
    style={{
      width: "100%",
      maxWidth: wide ? 1520 : 1320,
      margin: "0 auto",
      padding: "24px 28px 32px",
      boxSizing: "border-box",
      ...style,
    }}
  >
    {children}
  </div>
);

// ── Status badge helper ───────────────────────────────────────────────────
const statusColors = {
  applied:         { bg: "#dbeafe", text: "#1d4ed8" },
  under_review:    { bg: "#fef3c7", text: "#92400e" },
  shortlisted:     { bg: "#d1fae5", text: "#065f46" },
  invited:         { bg: "#ede9fe", text: "#5b21b6" },
  accepted:        { bg: "#d1fae5", text: "#065f46" },
  rejected:        { bg: "#fee2e2", text: "#991b1b" },
  expired:         { bg: "#f3f4f6", text: "#6b7280" },
  draft:           { bg: "#f3f4f6", text: "#6b7280" },
  published:       { bg: "#dbeafe", text: "#1d4ed8" },
  registration_open:   { bg: "#d1fae5", text: "#065f46" },
  registration_closed: { bg: "#fef3c7", text: "#92400e" },
  under_review_ev: { bg: "#fef3c7", text: "#92400e" },
  invitations_released: { bg: "#ede9fe", text: "#5b21b6" },
  completed:       { bg: "#d1fae5", text: "#065f46" },
  archived:        { bg: "#f3f4f6", text: "#6b7280" },
  pending:         { bg: "#fef3c7", text: "#92400e" },
  not_invited:     { bg: "#f3f4f6", text: "#6b7280" },
  withdrawn:       { bg: "#fee2e2", text: "#991b1b" },
};

const Badge = ({ status, label }) => {
  const s = statusColors[status] || { bg: "#f3f4f6", text: "#6b7280" };
  return (
    <span style={{
      background: s.bg, color: s.text,
      padding: "2px 10px", borderRadius: 99,
      fontSize: 11, fontWeight: 700, letterSpacing: .4,
      textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      {label || status?.replace(/_/g, " ")}
    </span>
  );
};

// ── Shared card wrapper ────────────────────────────────────────────────────
const Card = ({ children, style = {}, className = "" }) => (
  <div style={{
    background: C.card,
    borderRadius: 18,
    border: `1px solid rgba(148,163,184,.22)`,
    boxShadow: "0 16px 40px rgba(15,23,42,.06)",
    ...style
  }} className={className}>
    {children}
  </div>
);

// ── Stat card ─────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color, sub }) => (
  <Card style={{ padding: "20px 22px", flex: 1, minWidth: 160 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: .8, textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 32, fontWeight: 900, color: C.text, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>}
      </div>
      <div style={{ background: color + "14", borderRadius: 14, padding: 12 }}>
        <Icon size={24} color={color} />
      </div>
    </div>
  </Card>
);

// ── Section header ─────────────────────────────────────────────────────────
const SectionHeader = ({ title, sub, action }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
    <div style={{ minWidth: 0 }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: C.text, letterSpacing: -.2 }}>{title}</h2>
      {sub && <p style={{ margin: "4px 0 0", fontSize: 13, color: C.muted, lineHeight: 1.45 }}>{sub}</p>}
    </div>
    {action}
  </div>
);

// ── Input wrapper ─────────────────────────────────────────────────────────
const Field = ({ label, required, children, hint }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
      {label} {required && <span style={{ color: C.danger }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 11, color: C.muted, margin: "4px 0 0" }}>{hint}</p>}
  </div>
);

const Input = ({ ...props }) => (
  <input {...props} style={{
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text,
    background: "#fff", outline: "none", boxSizing: "border-box",
    transition: "border .2s",
    ...props.style,
  }} onFocus={e => e.target.style.borderColor = C.accent}
     onBlur={e => e.target.style.borderColor = C.border} />
);

const Select = ({ children, ...props }) => (
  <select {...props} style={{
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text,
    background: "#fff", outline: "none", boxSizing: "border-box",
    ...props.style,
  }}>
    {children}
  </select>
);

const Textarea = ({ ...props }) => (
  <textarea {...props} style={{
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text,
    background: "#fff", outline: "none", boxSizing: "border-box",
    resize: "vertical", minHeight: 80,
    ...props.style,
  }} />
);

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6,
    borderRadius: 10, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
    border: "none", transition: "all .15s", opacity: disabled ? .6 : 1,
    ...style,
  };
  const variants = {
    primary: { background: C.accent, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    secondary: { background: C.light, color: C.primary, padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    danger: { background: C.danger, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    success: { background: C.success, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    ghost: { background: "transparent", color: C.accent, padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    outline: { background: "transparent", color: C.primary, border: `1.5px solid ${C.border}`, padding: size === "sm" ? "5px 13px" : "9px 19px", fontSize: size === "sm" ? 12 : 14 },
  };
  return (
    <button style={{ ...base, ...variants[variant] }} onClick={onClick} disabled={disabled}>
      {Icon && <Icon size={size === "sm" ? 13 : 15} />}
      {children}
    </button>
  );
};

// ── Mini bar chart (CSS only) ──────────────────────────────────────────────
const BarChart = ({ data, color = C.accent }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 80, paddingTop: 8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{
            width: "100%", height: `${(d.value / max) * 64}px`,
            background: color, borderRadius: "4px 4px 0 0",
            minHeight: 4, transition: "height .4s",
          }} />
          <span style={{ fontSize: 9, color: C.muted, textAlign: "center", lineHeight: 1.2 }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
};

// ── Donut chart (SVG) ─────────────────────────────────────────────────────
const DonutChart = ({ segments, size = 100 }) => {
  const r = 40, cx = 50, cy = 50;
  const circumference = 2 * Math.PI * r;
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.light} strokeWidth={16} />
      {segments.map((seg, i) => {
        const dash = (seg.value / total) * circumference;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={seg.color} strokeWidth={16}
            strokeDasharray={`${dash} ${circumference - dash}`}
            strokeDashoffset={-offset}
            style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
          />
        );
        offset += dash;
        return el;
      })}
      <text x="50" y="54" textAnchor="middle" fontSize="14" fontWeight="800" fill={C.text}>
        {total}
      </text>
    </svg>
  );
};

// ── Progress bar ──────────────────────────────────────────────────────────
const ProgressBar = ({ value, color = C.accent, height = 8 }) => (
  <div style={{ background: C.light, borderRadius: 99, height, overflow: "hidden" }}>
    <div style={{ width: `${Math.min(value, 100)}%`, height: "100%", background: color, borderRadius: 99, transition: "width .5s" }} />
  </div>
);

// ── Empty state ───────────────────────────────────────────────────────────
const EmptyState = ({ icon: Icon = FileText, title, sub, action }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "72px 24px", gap: 12, textAlign: "center", minHeight: 240 }}>
    <div style={{ background: C.light, borderRadius: 18, padding: 20 }}>
      <Icon size={32} color={C.muted} />
    </div>
    <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{title}</div>
    <div style={{ fontSize: 13, color: C.muted, maxWidth: 280 }}>{sub}</div>
    {action}
  </div>
);

// ── Loading skeleton ──────────────────────────────────────────────────────
const Skeleton = ({ h = 16, w = "100%", style = {} }) => (
  <div style={{
    height: h, width: w, borderRadius: 6, background: "linear-gradient(90deg,#e8eef5 25%,#f5f7fa 50%,#e8eef5 75%)",
    backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite",
    ...style,
  }} />
);

// ── Countdown timer ───────────────────────────────────────────────────────
const Countdown = ({ deadline }) => {
  const [diff, setDiff] = useState(0);
  useEffect(() => {
    const update = () => setDiff(Math.max(0, new Date(deadline) - Date.now()));
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [deadline]);
  const days = Math.floor(diff / 86400000);
  const hrs  = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {[["D", days], ["H", hrs], ["M", mins]].map(([u, v]) => (
        <div key={u} style={{ background: C.primary, color: "#fff", borderRadius: 6, padding: "4px 8px", textAlign: "center", minWidth: 36 }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>{String(v).padStart(2, "0")}</div>
          <div style={{ fontSize: 9, opacity: .7 }}>{u}</div>
        </div>
      ))}
    </div>
  );
};

// ── Mock data - COMMENTED OUT FOR PRODUCTION ────────────────────────────────
/*
const MOCK_EVENTS = [...];
const MOCK_APPLICATIONS = [...];
const MOCK_INVITATIONS = [...];
const MOCK_APPLICANTS = [...];

// Using production services: Fetchexecutequery, Fetchheaderdata, Fetchupdatequery, CreateData
*/

const FALLBACK_EVENT = {
  event_name: "Global EdTech Summit 2025",
  event_category: "Summit",
  industry: "Education",
  city: "Dubai",
  country: "UAE",
  organizer: "EduWorld Org",
  banner_color: "#2563eb",
  event_date: "2025-11-15",
  available_slots: 120,
  registration_close_date: "2025-10-01",
  registration_open_date: "2025-08-01",
  event_status: "registration_open",
};

const FALLBACK_EVENTS = [
  FALLBACK_EVENT,
  {
    event_name: "STEM Innovation Expo",
    event_category: "Expo",
    industry: "Technology",
    city: "Singapore",
    country: "Singapore",
    organizer: "Future Learning Network",
    banner_color: "#0f766e",
    event_date: "2025-12-05",
    available_slots: 80,
    registration_close_date: "2025-11-10",
    registration_open_date: "2025-09-10",
    event_status: "published",
  },
];

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 1 – FORM FILLER DASHBOARD
// ══════════════════════════════════════════════════════════════════════════
export function FormFillerDashboard() {
const uc = getUserCredentials();
const [loading, setLoading] = useState(true);

useEffect(() => {
const fetchData = async () => {
try {
setTimeout(() => setLoading(false), 900);
} catch (err) {
setLoading(false);
}
};


fetchData();


}, []);

const monthData = [
{ label: "Mar", value: 2 },
{ label: "Apr", value: 5 },
{ label: "May", value: 3 },
{ label: "Jun", value: 7 },
{ label: "Jul", value: 4 },
{ label: "Aug", value: 8 }
];

const invSegments = [
{ color: C.success, value: 3, label: "Accepted" },
{ color: C.accent, value: 2, label: "Pending" },
{ color: C.danger, value: 1, label: "Rejected" }
];

return (
<AppShell>
  <PageWrap wide style={{ paddingTop: 20 }}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 18, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 18 }}>
      <div style={{ minWidth: 260 }}>
        <div style={{ fontSize: 11, letterSpacing: .8, textTransform: "uppercase", color: C.muted, fontWeight: 800 }}>Form Filler Dashboard</div>
        <h1 style={{ margin: "6px 0 6px", fontSize: 28, lineHeight: 1.1, color: C.text }}>Welcome back, {uc.user_name || "User"} 👋</h1>
        <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>Track applications, invitations, and profile health from a single operational view.</p>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Badge status="published" label="Active Profile" />
        <Badge status="pending" label="2 actions due" />
        <Badge status="accepted" label="3 accepted" />
      </div>
    </div>

    <div style={{ display: "grid", gap: 18 }}>
      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} h={92} style={{ borderRadius: 16 }} />
          ))}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <StatCard label="Events" value="24" icon={Calendar} color={C.accent} sub="3 new this week" />
          <StatCard label="Applications" value="12" icon={FileText} color={C.purple} sub="Submitted" />
          <StatCard label="Invitations" value="4" icon={Bell} color={C.warn} sub="Received" />
          <StatCard label="Accepted" value="3" icon={CheckCircle} color={C.success} sub="Success" />
          <StatCard label="Upcoming" value="2" icon={Clock} color={C.accent2} sub="Next 30 days" />
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2.1fr) minmax(320px, .9fr)", gap: 18, alignItems: "start" }}>
        <div style={{ display: "grid", gap: 18 }}>
          <Card style={{ padding: 20, minHeight: 340 }}>
            <SectionHeader
              title="Recent Events"
              sub="Matching your school profile"
              action={<Btn variant="ghost" size="sm" icon={ArrowRight}>View All</Btn>}
            />
            <EmptyState title="No Events" sub="No matching events available right now." />
          </Card>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
            <Card style={{ padding: 20 }}>
              <SectionHeader title="Applications Trend" sub="Monthly submission activity" />
              <BarChart data={monthData} color={C.accent} />
            </Card>

            <Card style={{ padding: 20 }}>
              <SectionHeader title="Recommended Events" sub="Curated opportunities" />
              <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
                <div style={{ padding: "10px 12px", background: C.light, borderRadius: 10 }}>🇺🇸 STEM Summit 2026</div>
                <div style={{ padding: "10px 12px", background: C.light, borderRadius: 10 }}>🇬🇧 Global Education Expo</div>
                <div style={{ padding: "10px 12px", background: C.light, borderRadius: 10 }}>🇨🇦 International Career Fair</div>
                <div style={{ padding: "10px 12px", background: C.light, borderRadius: 10 }}>🇦🇺 Future Leaders Forum</div>
              </div>
            </Card>
          </div>
        </div>

        <div style={{ display: "grid", gap: 18, alignContent: "start" }}>
          <Card style={{ padding: 20, minHeight: 240 }}>
            <SectionHeader title="Invitations" sub="Pending invitations queue" />
            <EmptyState title="No Invitations" sub="No pending invitations at the moment." />
          </Card>

          <Card style={{ padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 800, letterSpacing: .8, textTransform: "uppercase" }}>Profile Health</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: C.warn, lineHeight: 1.1, marginTop: 4 }}>72%</div>
              </div>
              <Btn size="sm">Complete</Btn>
            </div>
            <div style={{ marginTop: 10 }}>
              <ProgressBar value={72} color={C.warn} />
            </div>
          </Card>

          <Card style={{ padding: 20 }}>
            <SectionHeader title="Invitation Status" sub="Current distribution" />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <DonutChart segments={invSegments} size={104} />
            </div>
          </Card>
        </div>
      </div>
    </div>

    <style>{`
      @keyframes shimmer {
        0% { background-position:200% 0; }
        100% { background-position:-200% 0; }
      }
    `}</style>
  </PageWrap>
</AppShell>
);
}


// ══════════════════════════════════════════════════════════════════════════
// SCREEN 2 – FORM FILLER PROFILE CREATION
// ══════════════════════════════════════════════════════════════════════════
export function ProfileCreation() {
  const uc = getUserCredentials();
  const [formData, setFormData] = useState({
    school_name: "St. Xavier's High School", city: "Mumbai", state: "Maharashtra",
    country: "India", website: "https://stxaviers.edu.in", contact_number: "+91-22-12345678",
    email: "contact@stxaviers.edu.in", school_type: "Private",
    rep1_name: "Priya Sharma", rep1_contact: "+91-9876543210", rep1_email: "priya@stxaviers.edu.in",
    linkedin_url: "", facebook_url: "", instagram_url: "",
  });
  const [additionalReps, setAdditionalReps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fields = Object.keys(formData);
  const filled = fields.filter(f => formData[f]).length;
  const completion = Math.round((filled / fields.length) * 100);

  const handleChange = (name, value) => setFormData(p => ({ ...p, [name]: value }));
  const addRep = () => setAdditionalReps(p => [...p, { name: "", contact: "", email: "" }]);
  const removeRep = (i) => setAdditionalReps(p => p.filter((_, idx) => idx !== i));

  const handleSave = async (status) => {
    setSaving(true);
    // In production: axios.post(CreateData()[1], { database: 'event_platform', table: 'school_profiles', formData: { ...formData, user_id: uc.user_id, tenant_id: uc.tenants_id, entity_id: uc.selectedentity, profile_status: status, completion_percentage: completion, additional_reps: JSON.stringify(additionalReps), created_by: uc.user_id, created_on: getFormattedDate() } }, CreateData()[0])
    setTimeout(() => { setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 3000); }, 1000);
  };

  const SectionTitle = ({ icon: Icon, title }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 24px", background: C.light, borderRadius: "8px 8px 0 0", borderBottom: `1px solid ${C.border}` }}>
      <Icon size={16} color={C.accent} />
      <span style={{ fontWeight: 800, fontSize: 14, color: C.primary }}>{title}</span>
    </div>
  );

  return (
    <AppShell>
      <PageWrap>
        <SectionHeader
          title="School Profile"
          sub="Build a complete institutional profile"
          action={<Badge status="published" label={`User #${uc.user_id}`} />}
        />

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 280px", gap: 24, alignItems: "start" }}>
          {/* Main form */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 20 }}>

            {/* School Information */}
            <Card>
              <SectionTitle icon={Building} title="School Information" />
              <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ gridColumn: "1/-1" }}>
                  <Field label="School Name" required>
                    <Input value={formData.school_name} onChange={e => handleChange("school_name", e.target.value)} placeholder="Enter school name" />
                  </Field>
                </div>
                <Field label="City" required><Input value={formData.city} onChange={e => handleChange("city", e.target.value)} /></Field>
                <Field label="State"><Input value={formData.state} onChange={e => handleChange("state", e.target.value)} /></Field>
                <Field label="Country" required>
                  <Select value={formData.country} onChange={e => handleChange("country", e.target.value)}>
                    {["India","UAE","Singapore","UK","USA","Australia","Canada"].map(c => <option key={c}>{c}</option>)}
                  </Select>
                </Field>
                <Field label="School Type" required>
                  <Select value={formData.school_type} onChange={e => handleChange("school_type", e.target.value)}>
                    {["Government","Private","International","Aided","Unaided"].map(t => <option key={t}>{t}</option>)}
                  </Select>
                </Field>
                <div style={{ gridColumn: "1/-1" }}>
                  <Field label="Website"><Input value={formData.website} onChange={e => handleChange("website", e.target.value)} placeholder="https://" /></Field>
                </div>
                <Field label="Contact Number"><Input value={formData.contact_number} onChange={e => handleChange("contact_number", e.target.value)} /></Field>
                <Field label="Email" required><Input type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} /></Field>
              </div>
            </Card>

            {/* Representative Information */}
            <Card>
              <SectionTitle icon={Users} title="Primary Representative" />
              <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                <Field label="Full Name" required><Input value={formData.rep1_name} onChange={e => handleChange("rep1_name", e.target.value)} /></Field>
                <Field label="Contact Number" required><Input value={formData.rep1_contact} onChange={e => handleChange("rep1_contact", e.target.value)} /></Field>
                <Field label="Email" required><Input type="email" value={formData.rep1_email} onChange={e => handleChange("rep1_email", e.target.value)} /></Field>
              </div>
            </Card>

            {/* Additional Reps */}
            <Card>
              <div style={{ padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <UserPlus size={16} color={C.accent} />
                  <span style={{ fontWeight: 800, fontSize: 14, color: C.primary }}>Additional Representatives</span>
                </div>
                <Btn size="sm" variant="outline" icon={Plus} onClick={addRep}>Add Representative</Btn>
              </div>
              <div style={{ padding: 24 }}>
                {additionalReps.length === 0 && (
                  <div style={{ textAlign: "center", color: C.muted, fontSize: 13, padding: "20px 0" }}>No additional representatives added</div>
                )}
                {additionalReps.map((rep, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 12, marginBottom: 12, padding: 14, background: C.bg, borderRadius: 8 }}>
                    <Input placeholder="Name" value={rep.name} onChange={e => { const r = [...additionalReps]; r[i].name = e.target.value; setAdditionalReps(r); }} />
                    <Input placeholder="Contact" value={rep.contact} onChange={e => { const r = [...additionalReps]; r[i].contact = e.target.value; setAdditionalReps(r); }} />
                    <Input placeholder="Email" value={rep.email} onChange={e => { const r = [...additionalReps]; r[i].email = e.target.value; setAdditionalReps(r); }} />
                    <button onClick={() => removeRep(i)} style={{ background: C.danger + "18", border: "none", borderRadius: 8, padding: "0 10px", cursor: "pointer", color: C.danger }}>
                      <Minus size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </Card>

            {/* Documents */}
            <Card>
              <SectionTitle icon={Paperclip} title="Document Uploads" />
              <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  { label: "School Brochure", hint: "PDF or DOCX, max 10MB", key: "brochure" },
                  { label: "Accreditation Documents", hint: "PDF only, max 5MB", key: "accreditation" },
                  { label: "School Logo", hint: "PNG or JPG, 200×200 recommended", key: "logo" },
                ].map(doc => (
                  <div key={doc.key} style={{ display: "flex", alignItems: "center", gap: 16, padding: 14, background: C.bg, borderRadius: 10, border: `1.5px dashed ${C.border}` }}>
                    <Upload size={18} color={C.muted} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{doc.label}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{doc.hint}</div>
                    </div>
                    <Btn size="sm" variant="outline" icon={Upload}>Choose File</Btn>
                  </div>
                ))}
              </div>
            </Card>

            {/* Social */}
            <Card>
              <SectionTitle icon={Globe} title="Social Media" />
              <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  { label: "LinkedIn", key: "linkedin_url", placeholder: "https://linkedin.com/school/..." },
                  { label: "Facebook", key: "facebook_url", placeholder: "https://facebook.com/..." },
                  { label: "Instagram", key: "instagram_url", placeholder: "https://instagram.com/..." },
                ].map(s => (
                  <Field key={s.key} label={s.label}>
                    <div style={{ display: "flex", gap: 0 }}>
                      <div style={{ background: C.light, border: `1.5px solid ${C.border}`, borderRight: "none", borderRadius: "8px 0 0 8px", padding: "9px 12px", display: "flex", alignItems: "center" }}>
                        <Link size={14} color={C.muted} />
                      </div>
                      <Input value={formData[s.key]} onChange={e => handleChange(s.key, e.target.value)} placeholder={s.placeholder} style={{ borderRadius: "0 8px 8px 0" }} />
                    </div>
                  </Field>
                ))}
              </div>
            </Card>

            {/* Actions */}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", paddingBottom: 32 }}>
              <Btn variant="outline" icon={Save} onClick={() => handleSave("draft")} disabled={saving}>Save Draft</Btn>
              <Btn variant="primary" icon={Check} onClick={() => handleSave("submitted")} disabled={saving}>
                {saving ? "Saving..." : "Submit Profile"}
              </Btn>
              {saved && <span style={{ display: "flex", alignItems: "center", gap: 6, color: C.success, fontWeight: 700, fontSize: 13 }}><CheckCircle size={14} /> Saved!</span>}
            </div>
          </div>

          {/* Sidebar: completion */}
          <div style={{ width: "100%", minWidth: 0 }}>
            <Card style={{ padding: 20, position: "sticky", top: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 12 }}>Profile Completion</div>
              <div style={{ textAlign: "center", marginBottom: 12 }}>
                <DonutChart segments={[{ color: C.accent, value: completion }, { color: C.light, value: 100 - completion }]} size={90} />
                <div style={{ fontSize: 18, fontWeight: 900, color: C.text, marginTop: 4 }}>{completion}%</div>
              </div>
              <ProgressBar value={completion} color={C.accent} height={6} />
              <div style={{ marginTop: 16, fontSize: 12, color: C.muted, fontWeight: 600 }}>MISSING FIELDS</div>
              {["Accreditation Documents", "Instagram URL", "LinkedIn URL"].map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                  <AlertCircle size={12} color={C.warn} />
                  <span style={{ fontSize: 12, color: C.text }}>{f}</span>
                </div>
              ))}
            </Card>
          </div>
        </div>
      </PageWrap>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 3 – EVENT LISTING PAGE
// ══════════════════════════════════════════════════════════════════════════
export function EventListing() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ country: "", event_type: "" });
  const [view, setView] = useState("grid");
  const [interested, setInterested] = useState({});
  const events = FALLBACK_EVENTS;
  const publishedEvents = events.filter(ev => ev.event_status === "published");

  const filtered = publishedEvents.filter(ev => {
    const q = search.toLowerCase();
    return (
      (!q || ev.event_name.toLowerCase().includes(q) || ev.organizer.toLowerCase().includes(q)) &&
      (!filters.country || ev.country === filters.country) &&
      (!filters.event_type || ev.event_category === filters.event_type)
    );
  });

  return (
    <AppShell>
      <PageWrap wide>
        <SectionHeader
          title="Event Marketplace"
          sub="Discover and apply to events that match your school profile and interests."
          action={<div style={{ minWidth: 320, maxWidth: 520, width: "100%", position: "relative" }}>
            <Search size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: C.muted }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search events or organizers..."
              style={{ width: "100%", padding: "12px 14px 12px 40px", borderRadius: 14, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: "border-box", outline: "none", background: "#fff" }}
            />
          </div>}
        />

        {/* Filter bar */}
        <Card style={{ padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <Filter size={15} color={C.muted} />
          <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>Filters:</span>
          {[
            { label: "Country", key: "country", opts: ["India","UAE","Singapore","UK","USA"] },
            { label: "Event Type", key: "event_type", opts: ["Conference","Fair","Summit","Expo","Leadership"] },
          ].map(f => (
            <Select key={f.key} value={filters[f.key]} onChange={e => setFilters(p => ({ ...p, [f.key]: e.target.value }))} style={{ width: 150 }}>
              <option value="">All {f.label}s</option>
              {f.opts.map(o => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
            </Select>
          ))}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button onClick={() => setView("grid")} style={{ background: view === "grid" ? C.accent : C.light, border: "none", borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: view === "grid" ? "#fff" : C.text }}><Grid size={14} /></button>
            <button onClick={() => setView("list")} style={{ background: view === "list" ? C.accent : C.light, border: "none", borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: view === "list" ? "#fff" : C.text }}><List size={14} /></button>
          </div>
          <div style={{ fontSize: 13, color: C.muted }}>{filtered.length} events found</div>
        </Card>

        {/* Event cards */}
        <div style={{ display: view === "grid" ? "grid" : "flex", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", flexDirection: "column", gap: 20 }}>
          {filtered.map(ev => (
            <Card key={ev.id} style={{ overflow: "hidden" }}>
              {/* Banner */}
              <div style={{ height: 120, background: `linear-gradient(135deg, ${ev.banner_color}, ${ev.banner_color}99)`, display: "flex", alignItems: "flex-end", padding: 16, position: "relative" }}>
                <div style={{ position: "absolute", top: 12, right: 12 }}>
                  <Badge status={ev.event_status} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,.8)", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>{ev.event_category}</div>
                  <div style={{ fontSize: 17, fontWeight: 900, color: "#fff", lineHeight: 1.2 }}>{ev.event_name}</div>
                </div>
              </div>
              <div style={{ padding: "16px 18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted, marginBottom: 6 }}>
                  <Building size={12} /> {ev.organizer}
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 12, color: C.muted, marginBottom: 12 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><MapPin size={11} />{ev.city}, {ev.country}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Calendar size={11} />{ev.event_date}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: C.text }}>
                    <span style={{ fontWeight: 700 }}>{ev.available_slots}</span> slots available
                  </div>
                  <div style={{ fontSize: 12, color: C.muted }}>Deadline: {ev.registration_close_date}</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn variant="primary" size="sm" style={{ flex: 1 }}>Apply Now</Btn>
                  <button onClick={() => setInterested(p => ({ ...p, [ev.id]: !p[ev.id] }))}
                    style={{ background: interested[ev.id] ? C.warn + "18" : C.light, border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: interested[ev.id] ? C.warn : C.muted }}>
                    <Star size={13} fill={interested[ev.id] ? C.warn : "none"} />
                  </button>
                  <button style={{ background: C.light, border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: C.muted }}>
                    <Share2 size={13} />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
        {filtered.length === 0 && <EmptyState icon={Search} title="No events found" sub="Try adjusting your search or filters" />}
      </PageWrap>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 4 – EVENT DETAILS PAGE
// ══════════════════════════════════════════════════════════════════════════
export function EventDetails() {
  const uc = getUserCredentials();
  const [event, setEvent] = useState({});
  const ev = Object.keys(event || {}).length ? event : FALLBACK_EVENT;
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const timeline = [
    { label: "Registration Opens", date: "2025-08-01", status: "completed", icon: CheckCircle, color: C.success },
    { label: "Registration Closes", date: "2025-10-01", status: "active", icon: Clock, color: C.accent },
    { label: "Review Period", date: "2025-10-05 – 10-15", status: "upcoming", icon: Eye, color: C.muted },
    { label: "Invitation Release", date: "2025-10-20", status: "upcoming", icon: Send, color: C.muted },
    { label: "Event Day", date: "2025-11-15", status: "upcoming", icon: Flag, color: C.muted },
  ];

  const agenda = [
    { time: "09:00 AM", title: "Opening Ceremony & Welcome Address", speaker: "Dr. James Holton" },
    { time: "10:00 AM", title: "Keynote: Future of Education Technology", speaker: "Prof. Aisha Patel" },
    { time: "11:30 AM", title: "Panel Discussion: EdTech in Emerging Markets", speaker: "Various" },
    { time: "02:00 PM", title: "Workshop: AI Tools for Classrooms", speaker: "TechLearn Team" },
    { time: "04:00 PM", title: "Networking & Exhibition", speaker: "" },
  ];

  const faqs = [
    { q: "Who can apply?", a: "Schools and educational institutions from any country are eligible." },
    { q: "How many representatives can attend?", a: "Up to 3 representatives per institution." },
    { q: "Is there a registration fee?", a: "No, participation is free for selected institutions." },
  ];

  const tabs = ["overview", "agenda", "eligibility", "venue", "faq"];

  return (
    <AppShell>
      <PageWrap style={{ maxWidth: 1240, display: "grid", gridTemplateColumns: "minmax(0, 1fr) 320px", gap: 24, paddingTop: 28 }}>
        <div>
          <SectionHeader
            title={ev.event_name}
            sub={`${ev.organizer} · ${ev.city}, ${ev.country} · ${ev.available_slots} slots`}
            action={
              <div style={{ display: "flex", gap: 10 }}>
                <Badge status={ev.event_status} />
                <Btn variant="secondary" size="sm" icon={Bookmark} onClick={() => setSaved(p => !p)}>
                  {saved ? "Saved" : "Save Event"}
                </Btn>
                <Btn variant="primary" icon={ArrowRight}>Apply Now</Btn>
              </div>
            }
          />
          <Card style={{ padding: 0, marginBottom: 20, overflow: "hidden" }}>
            <div style={{ padding: "0 12px" }}>
              <div style={{ display: "flex", gap: 0, overflowX: "auto" }}>
                {tabs.map(t => (
                  <button key={t} onClick={() => setActiveTab(t)} style={{
                    padding: "14px 20px", border: "none", background: "none", cursor: "pointer",
                    fontWeight: activeTab === t ? 800 : 500, color: activeTab === t ? C.accent : C.muted,
                    borderBottom: `2px solid ${activeTab === t ? C.accent : "transparent"}`, fontSize: 13, textTransform: "capitalize",
                  }}>{t}</button>
                ))}
              </div>
            </div>
          </Card>

          {/* Timeline */}
          <Card style={{ padding: 24, marginBottom: 20 }}>
            <SectionHeader title="Event Timeline" />
            <div style={{ display: "flex", alignItems: "flex-start", gap: 0, overflowX: "auto" }}>
              {timeline.map((t, i) => (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                  {i < timeline.length - 1 && (
                    <div style={{ position: "absolute", top: 14, left: "50%", width: "100%", height: 2, background: t.status === "completed" ? C.success : C.border, zIndex: 0 }} />
                  )}
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: t.color, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, border: "2px solid #fff" }}>
                    <t.icon size={12} color="#fff" />
                  </div>
                  <div style={{ marginTop: 8, textAlign: "center", fontSize: 11, fontWeight: 700, color: t.status === "upcoming" ? C.muted : C.text }}>{t.label}</div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{t.date}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Tab content */}
          {activeTab === "overview" && (
            <Card style={{ padding: 24 }}>
              <SectionHeader title="Event Overview" />
              <p style={{ color: C.text, fontSize: 14, lineHeight: 1.7 }}>
                The Global EdTech Summit 2025 brings together leading educational institutions, innovators, and technology providers to explore the future of education. This flagship event creates a platform for meaningful dialogue, collaboration, and partnership building across borders.
              </p>
              <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {[
                  { label: "Event Category", value: ev.event_category },
                  { label: "Industry", value: ev.industry },
                  { label: "Available Slots", value: ev.available_slots },
                  { label: "Registration Deadline", value: ev.registration_close_date },
                ].map(d => (
                  <div key={d.label} style={{ padding: 14, background: C.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>{d.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginTop: 4 }}>{d.value}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {activeTab === "agenda" && (
            <Card style={{ padding: 24 }}>
              <SectionHeader title="Event Agenda" />
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {agenda.map((a, i) => (
                  <div key={i} style={{ display: "flex", gap: 16, padding: "14px 0", borderBottom: i < agenda.length - 1 ? `1px solid ${C.light}` : "none" }}>
                    <div style={{ width: 70, fontSize: 12, fontWeight: 700, color: C.accent, flexShrink: 0 }}>{a.time}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{a.title}</div>
                      {a.speaker && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{a.speaker}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {activeTab === "faq" && (
            <Card style={{ padding: 24 }}>
              <SectionHeader title="Frequently Asked Questions" />
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {faqs.map((f, i) => (
                  <div key={i} style={{ padding: 16, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
                    <div style={{ display: "flex", gap: 10 }}>
                      <HelpCircle size={16} color={C.accent} style={{ flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{f.q}</div>
                        <div style={{ fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>{f.a}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {activeTab === "eligibility" && (
            <Card style={{ padding: 24 }}>
              <SectionHeader title="Eligibility Criteria" />
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {["School must be operational for at least 3 years","Must have minimum 200 enrolled students","Accreditation from recognized board required","At least one certified representative must attend"].map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 14px", background: C.light, borderRadius: 8 }}>
                    <Check size={15} color={C.success} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{c}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {activeTab === "venue" && (
            <Card style={{ padding: 24 }}>
              <SectionHeader title="Venue Information" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {[{ label: "Venue Name", value: "Dubai World Trade Centre" }, { label: "City", value: "Dubai" }, { label: "Country", value: "UAE" }, { label: "Hall", value: "Hall 7, Level 2" }].map(d => (
                  <div key={d.label} style={{ padding: 14, background: C.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", fontWeight: 600 }}>{d.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginTop: 4 }}>{d.value}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div>
          <Card style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.text, marginBottom: 14 }}>Apply to this Event</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {[["Registration Deadline", ev.registration_close_date], ["Available Slots", ev.available_slots], ["Event Date", ev.event_date]].map(([l, v]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: C.muted }}>{l}</span>
                  <span style={{ fontWeight: 700, color: C.text }}>{v}</span>
                </div>
              ))}
            </div>
            <Btn variant="primary" style={{ width: "100%", justifyContent: "center" }} icon={ArrowRight}>Apply Now</Btn>
            <div style={{ marginTop: 10 }}>
              <Btn variant="outline" style={{ width: "100%", justifyContent: "center" }} icon={Mail}>Contact Organizer</Btn>
            </div>
          </Card>

          <Card style={{ padding: 20 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 12 }}>Organizer</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: ev.banner_color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800 }}>E</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{ev.organizer}</div>
                <div style={{ fontSize: 12, color: C.muted }}>Event Organizer</div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: C.muted }}><Mail size={12} />organizer@example.com</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: C.muted }}><Phone size={12} />+971-4-000-0000</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: C.muted }}><Globe size={12} />www.eduworldorg.com</div>
            </div>
          </Card>
        </div>
      </PageWrap>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 5 – APPLICATION SUBMISSION
// ══════════════════════════════════════════════════════════════════════════
export function ApplicationSubmission() {
  const uc = getUserCredentials();
  const [event, setEvent] = useState({});
  const ev = Object.keys(event || {}).length ? event : FALLBACK_EVENT;
  const [numReps, setNumReps] = useState(2);
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!agreed) { alert("Please confirm before submitting."); return; }
    setSubmitting(true);
    // SQL: INSERT INTO event_platform.applications (event_id, applicant_user_id, applicant_entity_id, tenant_id, profile_id, num_representatives, additional_notes, application_status, applied_on, created_by)
    // VALUES (ev.id, uc.user_id, uc.selectedentity, uc.tenants_id, profile.id, numReps, notes, 'applied', NOW(), uc.user_id)
    setTimeout(() => { setSubmitting(false); setSubmitted(true); }, 1200);
  };

  if (submitted) {
    return (
      <AppShell style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Card style={{ padding: 48, textAlign: "center", maxWidth: 440 }}>
          <div style={{ width: 64, height: 64, background: C.success + "20", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <CheckCircle size={32} color={C.success} />
          </div>
          <h2 style={{ color: C.text, margin: "0 0 8px" }}>Application Submitted!</h2>
          <p style={{ color: C.muted, fontSize: 14 }}>Your application for <strong>{ev.event_name}</strong> has been submitted successfully. You'll be notified about the review status.</p>
          <Btn variant="primary" style={{ marginTop: 20 }} icon={ArrowRight}>View My Applications</Btn>
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageWrap style={{ maxWidth: 1240, paddingTop: 28 }}>
        <SectionHeader
          title="Submit Application"
          sub={`${ev.event_name} · Applicant: User #${uc.user_id}`}
        />

        {/* Steps indicator */}
        <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
          {["Review Profile", "Add Details", "Preview & Submit"].map((s, i) => (
            <div key={i} style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: i < 2 ? C.success : i === 2 ? (preview ? C.success : C.accent) : C.light, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                {i < 2 ? <Check size={12} /> : i + 1}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: i < 2 ? C.success : C.text }}>{s}</div>
              {i < 2 && <div style={{ flex: 1, height: 2, background: i < 1 ? C.success : C.border }} />}
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 300px", gap: 20, alignItems: "start" }}>
          <div>
            {/* Profile preview */}
            <Card style={{ marginBottom: 20 }}>
              <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                <Building size={15} color={C.accent} />
                <span style={{ fontWeight: 800, fontSize: 14, color: C.text }}>School Profile (Auto-filled)</span>
                <div style={{ marginLeft: "auto" }}><Badge status="applied" label="Profile Active" /></div>
              </div>
              <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[["School Name", "St. Xavier's High School"], ["City", "Mumbai, India"], ["School Type", "Private"], ["Representative", "Priya Sharma"], ["Email", "priya@stxaviers.edu.in"], ["Contact", "+91-9876543210"]].map(([l, v]) => (
                  <div key={l}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase" }}>{l}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginTop: 2 }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ padding: "0 20px 16px", display: "flex", gap: 10 }}>
                {["Brochure.pdf", "Accreditation.pdf", "Logo.png"].map(f => (
                  <div key={f} style={{ display: "flex", alignItems: "center", gap: 6, background: C.light, borderRadius: 6, padding: "4px 10px", fontSize: 11, color: C.text }}>
                    <Paperclip size={10} /> {f}
                  </div>
                ))}
              </div>
            </Card>

            {/* Editable fields */}
            <Card style={{ padding: 24, marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 16 }}>Application Details</div>
              <Field label="Number of Representatives Attending" required hint="Max 3 representatives per institution">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button onClick={() => setNumReps(p => Math.max(1, p - 1))} style={{ width: 32, height: 32, borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.light, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Minus size={14} />
                  </button>
                  <span style={{ fontSize: 20, fontWeight: 800, color: C.text, minWidth: 24, textAlign: "center" }}>{numReps}</span>
                  <button onClick={() => setNumReps(p => Math.min(3, p + 1))} style={{ width: 32, height: 32, borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.light, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Plus size={14} />
                  </button>
                </div>
              </Field>
              <Field label="Additional Notes" hint="Optional: Any specific requirements or information for the organizer">
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. We are particularly interested in the AI workshop sessions..." style={{ minHeight: 100 }} />
              </Field>
            </Card>

            {/* Confirmation */}
            <Card style={{ padding: 20, border: `1.5px solid ${agreed ? C.success : C.border}`, transition: "border .2s" }}>
              <label style={{ display: "flex", gap: 14, cursor: "pointer", alignItems: "flex-start" }}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 2 }} />
                <span style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>
                  I confirm that all information provided in my school profile is accurate and up-to-date. I agree to the event's participation terms and understand that misrepresentation may result in disqualification.
                </span>
              </label>
            </Card>

            {/* Actions */}
            <div style={{ display: "flex", gap: 12, marginTop: 20, justifyContent: "flex-end" }}>
              <Btn variant="outline" icon={Eye} onClick={() => setPreview(p => !p)}>Preview Application</Btn>
              <Btn variant="outline" icon={Save}>Save as Draft</Btn>
              <Btn variant="primary" icon={Send} onClick={handleSubmit} disabled={submitting || !agreed}>
                {submitting ? "Submitting..." : "Submit Application"}
              </Btn>
            </div>
          </div>

          {/* Event summary */}
          <div>
            <Card style={{ padding: 20 }}>
              <div style={{ height: 80, background: `linear-gradient(135deg, ${ev.banner_color}, #0f2040)`, borderRadius: 8, marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#fff", fontWeight: 900, fontSize: 13, textAlign: "center", padding: "0 12px" }}>{ev.event_name}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[["Organizer", ev.organizer], ["Location", `${ev.city}, ${ev.country}`], ["Event Date", ev.event_date], ["Deadline", ev.registration_close_date], ["Slots", ev.available_slots]].map(([l, v]) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span style={{ color: C.muted }}>{l}</span>
                    <span style={{ fontWeight: 700, color: C.text }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 14, padding: 12, background: C.light, borderRadius: 8, fontSize: 12, color: C.muted, textAlign: "center" }}>
                <AlertCircle size={12} style={{ marginRight: 4 }} />
                Submitting as User #{uc.user_id}<br />Entity #{uc.selectedentity}
              </div>
            </Card>
          </div>
        </div>
      </PageWrap>
    </AppShell>
  );
}
